import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { dataUrlToFile } from '@/hooks/useCamera';
import { createUuid } from '@/lib/uuid';

interface UploadResult {
  url: string;
  path: string;
}

async function requireUserId(): Promise<string> {
  const { data, error } = await supabase.auth.getUser();
  if (error || !data.user) {
    throw new Error('Sessão não encontrada. Faça login para enviar arquivos.');
  }
  return data.user.id;
}

function buildObjectName(scopeId: string, folder: string, ext: string): string {
  return `${scopeId}/${folder}/${createUuid()}.${ext}`;
}

export function usePhotoUpload() {
  const [isUploading, setIsUploading] = useState(false);

  const uploadPhoto = async (
    dataUrl: string,
    bucket: string = 'quote-photos',
    folder: string = 'items',
    scopeId?: string,
  ): Promise<UploadResult | null> => {
    setIsUploading(true);

    try {
      const userId = await requireUserId();
      const objectName = buildObjectName(scopeId ?? userId, folder, 'jpg');
      const file = dataUrlToFile(dataUrl, objectName);

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(objectName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);

      return {
        url: urlData.publicUrl,
        path: data.path,
      };
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Erro ao fazer upload da foto.');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const uploadFile = async (
    file: File,
    bucket: string = 'quote-photos',
    folder: string = 'items',
    scopeId?: string,
  ): Promise<UploadResult | null> => {
    setIsUploading(true);

    try {
      const userId = await requireUserId();
      const ext = file.name.split('.').pop() || 'jpg';
      const objectName = buildObjectName(scopeId ?? userId, folder, ext);

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(objectName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Upload error:', error);
        throw error;
      }

      const { data: urlData } = supabase.storage.from(bucket).getPublicUrl(data.path);

      return {
        url: urlData.publicUrl,
        path: data.path,
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erro ao fazer upload do arquivo.');
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const deletePhoto = async (
    path: string,
    bucket: string = 'quote-photos'
  ): Promise<boolean> => {
    try {
      const { error } = await supabase.storage.from(bucket).remove([path]);
      if (error) {
        console.error('Delete error:', error);
        return false;
      }
      return true;
    } catch (error) {
      console.error('Error deleting photo:', error);
      return false;
    }
  };

  const uploadMultiplePhotos = async (
    dataUrls: string[],
    bucket: string = 'quote-photos',
    folder: string = 'general'
  ): Promise<UploadResult[]> => {
    setIsUploading(true);

    try {
      const results: UploadResult[] = [];
      for (const dataUrl of dataUrls) {
        const result = await uploadPhoto(dataUrl, bucket, folder);
        if (result) {
          results.push(result);
        }
      }
      return results;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadPhoto,
    uploadFile,
    deletePhoto,
    uploadMultiplePhotos,
    isUploading,
  };
}
